import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseBucketDate = (value) => {
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const withSeconds = normalized.length === 16 ? `${normalized}:00Z` : `${normalized}Z`;
  const date = new Date(withSeconds);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatBucketLabel = (value, interval) => {
  const date = parseBucketDate(value);
  if (!date) return value || '-';
  if (interval === 'day') {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short'
    }).format(date);
  }
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const TooltipContent = ({ active, payload, label, interval }) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 10,
        padding: '10px 12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        fontSize: 12
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {formatBucketLabel(label, interval)}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span>Logs</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
};

export default function LogsBarChart({ data, interval }) {
  const chartData = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    return arr.map((item) => ({
      bucket: item.bucket,
      logs: toNumber(item.logs)
    }));
  }, [data]);

  if (!chartData.length) {
    return <p className="admin-alert">Pas encore de données.</p>;
  }

  return (
    <div className="admin-bar-chart" style={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey="bucket"
            tickFormatter={(value) => formatBucketLabel(value, interval)}
            tickLine={false}
            axisLine={false}
            minTickGap={20}
          />
          <YAxis tickLine={false} axisLine={false} />
          <Tooltip content={<TooltipContent interval={interval} />} />
          <Bar dataKey="logs" fill="rgba(31, 42, 90, 0.9)" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
